<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-1">
        <i class="far fa-envelope"></i> Inbox
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-1">
        <DataTable ref="dataTable" :columns="columnList" :filterOptions="filterOptions" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="3" :url="dataURL" :hasDateRangeFilter="true">
          <template v-slot:title>
            <div class="row">
              <div class="col-2">
                <ContentTitle>Notifications</ContentTitle>
              </div>
              <div class="col-8 pt-1" v-if="isAnyVisibleNotificationsSelected()">
                <FormButton class="btn-white" @click="markSelectedRead()" v-if="!allSelectedNotificationsRead()">
                  <Tooltip label="Mark as read"><i class="far fa-envelope"></i></Tooltip>
                </FormButton>
                <FormButton class="btn-white" @click="markSelectedUnread()" v-if="allSelectedNotificationsRead()">
                  <Tooltip label="Mark as unread"><i class="far fa-envelope-open"></i></Tooltip>
                </FormButton>
              </div>
            </div>
          </template>
          <template v-slot:tableHeaderCell="tableHeaderCell">
          <span v-if="tableHeaderCell.data == 0" class="ml-1">
            <FormCheckbox :value="isAllVisibleNotificationsSelected()" @click="selectAllClicked()" />
          </span>
        </template>
          <template v-slot:row="row">
            <tr :key="row.data.id" :class="['notification-row', row.data.isRead ? 'notification-read' : 'notification-unread']">
              <td class="p-1">
                <span class="pr-3 pl-2">
                  <FormCheckbox :value="isNotificationSelected(row.data.id)" @change="updateNotificationSelection(row.data.id, $event)" />
                </span>
                <Tooltip :label="formatLookupName(row.data.type, NotificationTypeLookup)">
                  <i class="fas fa-envelope fa-fw notification-icon-message" v-if="row.data.type == NotificationTypes.message" />
                  <i class="fas fa-comment-exclamation fa-fw notification-icon-push" v-if="row.data.type == NotificationTypes.push" />
                </Tooltip>
              </td>
              <td @click.prevent="notificationClicked(row.data)">
                {{row.data.subject}}
              </td>
              <td @click.prevent="notificationClicked(row.data)">
                {{row.data.message}}
              </td>
              <td @click.prevent="notificationClicked(row.data)">
                {{formatNotificationTime(row.data.createdAt)}}
              </td>
            </tr>
          </template>
        </DataTable>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<style scoped>
.notification-icon-push {
  color: #cecece;
}
.notification-icon-message {
  color: #cecece;
}
.notification-unread .notification-icon-push {
  color: #686de0;
}
.notification-unread .notification-icon-message {
  color: #e98833;
}
.notification-row {
  cursor: pointer;
}
.notification-row.notification-unread {
  font-weight: bold;
}
</style>


<script>
import moment from 'moment'

import NotificationDialog from '@/modules/communication/views/notificationDialog'

import { NotificationTypes, NotificationTypeLookup } from '@/store/types'

export default {
  name: 'NotificationList',
  computed: {
    dataURL() {
      var params = {
        filter: {}
      }
      return '/communication/notifications?' + $.param(params)
    }
  },
  data() {
    return {
      selectedNotificationMap: {},
      columnList: [
        { title: "", data: "type" },
        { title: "Subject", data: "subject" },
        { title: "Message", data: "message" },
        { title: "Received", data: "createdAt", dateFilter: true, type: 'datetime' }
      ],
      filterOptions: [
        {
          field: 'type',
          name: 'Type',
          options: [
            { name: 'Push Notification', value: NotificationTypes.push, class: 'label-notification-push' },
            { name: 'Message', value: NotificationTypes.message, class: 'label-notification-message' },
          ]
        },
        {
          field: 'isRead',
          name: 'Read',
          options: [
            { name: 'Unread', value: 0, class: 'label-status-active' },
          ]
        }
      ],
    }
  },
  created() {
    this.NotificationTypes = NotificationTypes
    this.NotificationTypeLookup = NotificationTypeLookup
  },
  methods: {
    reloadData() {
      if(this.$refs.dataTable)
        this.$refs.dataTable.reload()
    },
    notificationRowClass(notification) {
      if(!notification.isRead)
        return 'font-weight-bold'
      return ''
    },
    notificationClicked(notification) {
      this.displayModal(NotificationDialog, { notificationId: notification.id })
      .then(result => {
        // Reload to update read status
        this.reloadData()
      })
    },
    formatNotificationTime(time) {
      let logTime = moment(time).local().startOf('day')
      let curTimeMoment = moment().local().startOf('day')

      let diffMonths = curTimeMoment.diff(logTime, 'months')

      if(logTime.isSame(curTimeMoment, 'days'))
        return this.formatTime(time)

      if(diffMonths < 6)
        return this.formatDateCustom(time, 'Do MMM')

      return this.formatDate(time)
    },
    getVisibleNotificationList() {
      if(!this.$refs.dataTable)
        return []
      return this.$refs.dataTable.getRowListData()
    },
    isAnyVisibleNotificationsSelected() {
      return this.getVisibleNotificationList().some(notification => this.selectedNotificationMap[notification.id])
    },
    allSelectedNotificationsRead() {
      let selectedCount = Object.keys(this.selectedNotificationMap).filter(notificationId => this.selectedNotificationMap[notificationId]).length
      let readCount = this.getVisibleNotificationList().filter(notification => this.selectedNotificationMap[notification.id] && notification.isRead).length

      return selectedCount == readCount
    },
    isAllVisibleNotificationsSelected() {
      let rowListData = this.getVisibleNotificationList()
      if(rowListData.length == 0)
        return false

      return !rowListData.some(notification => !this.selectedNotificationMap[notification.id])
    },
    selectAllClicked() {
      if(this.isAllVisibleNotificationsSelected())
        this.unselectAllVisible()
      else
        this.selectAllVisible()      
    },
    isNotificationSelected(notificationId) {
      return this.selectedNotificationMap[notificationId]
    },
    updateNotificationSelection(notificationId, selected) {
      this.$set(this.selectedNotificationMap, notificationId, selected)
    },
    selectAllVisible() {
      this.getVisibleNotificationList().forEach(notification => {
        this.$set(this.selectedNotificationMap, notification.id, true)
      })
    },
    unselectAllVisible() {
      this.getVisibleNotificationList().forEach(notification => {
        this.$set(this.selectedNotificationMap, notification.id, false)
      })
    },
    markSelectedUnread() {
      var updateList = Object.keys(this.selectedNotificationMap).filter(notificationId => this.selectedNotificationMap[notificationId]).map(notificationId => {
        return {
          id: notificationId,
          isRead: false
        }
      })
        
      this.$apiService.communicationService.updateNotificationBatch({ notificationList: updateList })
      .then(() => {
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err, 'Error updating notifications')
      })      
    },
    markSelectedRead() {
      var updateList = Object.keys(this.selectedNotificationMap).filter(notificationId => this.selectedNotificationMap[notificationId]).map(notificationId => {
        return {
          id: notificationId,
          isRead: true
        }
      })
        
      this.$apiService.communicationService.updateNotificationBatch({ notificationList: updateList })
      .then(() => {
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err, 'Error updating notifications')
      })   
    }
  }
}
</script>